// Tv.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useSocket } from "../context/SocketContext";
import { useAudioSettings } from "../context/AudioSettingsContext";

import mioSound from "../assets/mio.mp3";
import ventaTimeoutSound from "../assets/timeout.mp3";
import subastaTimeoutSound from "../assets/timeout.mp3";

const IDLE_AFTER_MS = 10_000;
const POOL_SIZE = 6;

export default function Tv() {
  const { socket, status } = useSocket();
  const { settings: audioSettings, mioCustomUrl, timeoutCustomUrl } = useAudioSettings();

  const [ventaState, setVentaState] = useState(null);
  const [subastaState, setSubastaState] = useState(null);

  const [mode, setMode] = useState("idle"); // idle | venta | subasta | ventaSold | ventaTimeout | subastaTimeout | subastaEnded
  const [now, setNow] = useState(Date.now());

  const idleTimerRef = useRef(null);
  const joinedRef = useRef(false);

  // Locks para mantener pantallas 10s
  const modeLockUntilRef = useRef(0);
  const modeLockNameRef = useRef(null);

  // Detectores de transición
  const prevVentaActiveRef = useRef(false);
  const lastVentaEndedKeyRef = useRef(null);

  const prevSubastaActiveRef = useRef(false);
  const lastSubastaEndedAtRef = useRef(null);
  const subastaEndedWinnerRef = useRef(null); // winner del evento timeout

  // ===== AUDIO =====
  const [audioReady, setAudioReady] = useState(false);

  const mkPool = (src) =>
    Array.from({ length: POOL_SIZE }, () => {
      const a = new Audio(src);
      a.preload = "auto";
      a.volume = 1;
      return a;
    });

  const playPool = (poolRef, idxRef) => {
    const pool = poolRef.current;
    if (!pool?.length) return;

    const a = pool[idxRef.current];
    idxRef.current = (idxRef.current + 1) % pool.length;

    try {
      a.pause();
      a.currentTime = 0;
      const p = a.play();
      if (p?.catch) p.catch((err) => console.log("[TV] play() bloqueado:", err));
    } catch {}
  };

  // Pools/índices (refs) — SOLO aquí (nunca dentro de funciones)
  const ventaTimeoutPoolRef = useRef(null);
  const subastaTimeoutPoolRef = useRef(null);
  const mioPoolRef = useRef(null);

  const vIdxRef = useRef(0);
  const sIdxRef = useRef(0);
  const mioIdxRef = useRef(0);

  // Inicializar pools al montar y recrear si cambia el sonido custom
  useEffect(() => {
    mioPoolRef.current = mkPool(mioCustomUrl || mioSound);
  }, [mioCustomUrl]);

  useEffect(() => {
    ventaTimeoutPoolRef.current   = mkPool(timeoutCustomUrl || ventaTimeoutSound);
    subastaTimeoutPoolRef.current = mkPool(timeoutCustomUrl || subastaTimeoutSound);
  }, [timeoutCustomUrl]);

  const unlockAudioNow = async () => {
    const audios = [
      ventaTimeoutPoolRef.current?.[0],
      subastaTimeoutPoolRef.current?.[0],
      mioPoolRef.current?.[0],
    ].filter(Boolean);

    try {
      for (const a of audios) {
        a.muted = true;
        await a.play();
        a.pause();
        a.currentTime = 0;
        a.muted = false;
      }
      setAudioReady(true);
    } catch {
      setAudioReady(false);
    }
  };

  const lockModeFor = (nextMode, ms) => {
    modeLockNameRef.current = nextMode;
    modeLockUntilRef.current = Date.now() + ms;

    setMode(nextMode);

    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      modeLockNameRef.current = null;
      modeLockUntilRef.current = 0;
      setMode("idle");
    }, ms);
  };

  // Intentar desbloqueo con cualquier interacción (si la hay)
  useEffect(() => {
    const handler = () => unlockAudioNow();
    window.addEventListener("pointerdown", handler);
    window.addEventListener("keydown", handler);

    return () => {
      window.removeEventListener("pointerdown", handler);
      window.removeEventListener("keydown", handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== BRAND =====
  const BRAND = useMemo(
    () => ({
      companyName: "Liquidaciones Granada",
      whatsapp: "611 00 74 28",
      subtitle: "Pedidos por WhatsApp",
    }),
    []
  );

  const BRAND_RED = "#e30613";
  const BRAND_WHITE = "#ffffff";

  // reloj local
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(t);
  }, []);

  // ===== SOCKET LISTENERS =====
  useEffect(() => {
    const s = socket.current;
    if (!s) return;

    const onSnapshot = (snap) => {
      if (snap?.venta !== undefined) setVentaState(snap.venta);
      if (snap?.subasta !== undefined) setSubastaState(snap.subasta);
    };

    const onVenta = (st) => setVentaState(st);
    const onSubasta = (st) => setSubastaState(st);

    const onMioSound = () => {
      if (audioSettings.mioMuted) return;
      mioPoolRef.current?.forEach(a => { a.volume = audioSettings.mioVolume; });
      playPool(mioPoolRef, mioIdxRef);
    };

    const onSubastaEnded = (payload) => {
      if (payload?.reason === "timeout") {
        if (!audioSettings.timeoutMuted) {
          subastaTimeoutPoolRef.current?.forEach(a => { a.volume = audioSettings.timeoutVolume; });
          playPool(subastaTimeoutPoolRef, sIdxRef);
        }
        // Si hay puja más alta, mostrar ganador provisional en vez de pantalla de timeout
        if (payload?.winner) {
          subastaEndedWinnerRef.current = payload.winner;
          lockModeFor("subastaEnded", 15_000);
        } else {
          subastaEndedWinnerRef.current = null;
          lockModeFor("subastaTimeout", 10_000);
        }
      }
    };

    s.on("display:snapshot", onSnapshot);
    s.on("venta:state", onVenta);
    s.on("subasta:state", onSubasta);
    const onSoundTest = ({ type }) => {
      if (type === "mio") {
        if (audioSettings.mioMuted) return;
        mioPoolRef.current?.forEach(a => { a.volume = audioSettings.mioVolume; });
        playPool(mioPoolRef, mioIdxRef);
      } else if (type === "timeout") {
        if (audioSettings.timeoutMuted) return;
        ventaTimeoutPoolRef.current?.forEach(a => { a.volume = audioSettings.timeoutVolume; });
        playPool(ventaTimeoutPoolRef, vIdxRef);
      }
    };

    s.on("mio:sound", onMioSound);
    s.on("subasta:ended", onSubastaEnded);
    s.on("sound:test", onSoundTest);

    const doJoin = () => {
      if (joinedRef.current) return;
      joinedRef.current = true;
      s.emit("join_display");
    };

    if (s.connected) doJoin();
    s.on("connect", doJoin);

    return () => {
      s.off("display:snapshot", onSnapshot);
      s.off("venta:state", onVenta);
      s.off("subasta:state", onSubasta);
      s.off("mio:sound", onMioSound);
      s.off("subasta:ended", onSubastaEnded);
      s.off("sound:test", onSoundTest);
      s.off("connect", doJoin);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.state, socket]);

  // ===== MODE LOGIC =====
  useEffect(() => {
    // 1) Si hay lock, NO toques timers ni recalc
    if (Date.now() < modeLockUntilRef.current) return;

    // 2) Solo si NO hay lock, puedes limpiar el idleTimer
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }

    // ---- detectar fin de VENTA por timeout (active true -> false + endedReason=timeout)
    const ventaWasActive = prevVentaActiveRef.current;
    const ventaIsActive = !!ventaState?.active;
    prevVentaActiveRef.current = ventaIsActive;

    if (ventaWasActive && !ventaIsActive && ventaState?.endedReason === "timeout") {
      const key = ventaState?.endsAt ?? ventaState?.startedAt ?? Date.now();
      const hasWinners = Array.isArray(ventaState?.winners) && ventaState.winners.length > 0;

      if (lastVentaEndedKeyRef.current !== key) {
        lastVentaEndedKeyRef.current = key;
        if (!hasWinners && !audioSettings.timeoutMuted) {
          ventaTimeoutPoolRef.current?.forEach(a => { a.volume = audioSettings.timeoutVolume; });
          playPool(ventaTimeoutPoolRef, vIdxRef);
        }
      }

      // Si hay al menos un ganador, mostrar ventaSold en vez de timeout
      if (hasWinners) {
        lockModeFor("ventaSold", 10_000);
      } else {
        lockModeFor("ventaTimeout", 10_000);
      }
      return;
    }

    // ---- subasta active tracking
    const subastaWasActive = prevSubastaActiveRef.current;
    const subastaIsActive = !!subastaState?.active;
    prevSubastaActiveRef.current = subastaIsActive;

    if (ventaState?.active) {
      setMode("venta");
      return;
    }

    if (subastaIsActive) {
      setMode("subasta");
      return;
    }

    // subasta terminó (fin normal detectado por transición)
    if (subastaWasActive && !subastaIsActive) {
      setVentaState(null);
      setMode("subastaEnded");
      idleTimerRef.current = setTimeout(() => setMode("idle"), IDLE_AFTER_MS);
      return;
    }

    // fallback: si llega leaderboard y endsAt -> consideramos ended
    const lb = Array.isArray(subastaState?.leaderboard) ? subastaState.leaderboard : [];
    const endedKey = subastaState?.endsAt ?? null;

    if (!subastaIsActive && lb.length > 0 && endedKey && lastSubastaEndedAtRef.current !== endedKey) {
      lastSubastaEndedAtRef.current = endedKey;
      setMode("subastaEnded");
      idleTimerRef.current = setTimeout(() => setMode("idle"), IDLE_AFTER_MS);
      return;
    }

    // venta sold
    if (ventaState && !ventaState.active && !prevSubastaActiveRef.current) {
      const winners = Array.isArray(ventaState.winners) ? ventaState.winners : [];
      if (winners.length > 0) {
        setMode("ventaSold");
        idleTimerRef.current = setTimeout(() => setMode("idle"), IDLE_AFTER_MS);
        return;
      }
    }

    setMode("idle");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ventaState, subastaState]);

  // ===== Computed =====
  const ventaProgress = useMemo(() => {
    if (!ventaState?.active) return 0;
    const startedAt = Number(ventaState.startedAt);
    const durationMs = Number(ventaState.durationMs);
    if (!Number.isFinite(startedAt) || !Number.isFinite(durationMs) || durationMs <= 0) return 0;
    const elapsed = Math.max(0, Math.min(durationMs, now - startedAt));
    return elapsed / durationMs;
  }, [ventaState, now]);

  const ventaRemaining = useMemo(() => {
    if (!ventaState?.active || !ventaState?.endsAt) return null;
    return Math.ceil(Math.max(0, ventaState.endsAt - now) / 1000);
  }, [ventaState, now]);

  const ventaPriceText = useMemo(() => {
    const p = ventaState?.price;
    if (p === null || p === undefined || !Number.isFinite(Number(p))) return "—";
    return `${Number(p)}€`;
  }, [ventaState]);

  const subastaRemaining = useMemo(() => {
    if (!subastaState?.active || !subastaState?.endsAt) return null;
    return Math.ceil(Math.max(0, subastaState.endsAt - now) / 1000);
  }, [subastaState, now]);

  const subastaProgress = useMemo(() => {
    if (!subastaState?.active || !subastaState?.endsAt) return 0;
    const windowMs = Number(subastaState.windowMs) || 30_000;
    if (!Number.isFinite(windowMs) || windowMs <= 0) return 0;
    const remainingMs = Math.max(0, subastaState.endsAt - now);
    const elapsed = Math.max(0, Math.min(windowMs, windowMs - remainingMs));
    return elapsed / windowMs;
  }, [subastaState, now]);

  const subastaMinBidText = useMemo(() => {
    const mb = subastaState?.minBid;
    if (mb === null || mb === undefined || !Number.isFinite(Number(mb))) return "—";
    return `${Number(mb)}€`;
  }, [subastaState]);

  // ===== UI styles =====
  const bgImage = mode === "idle" ? "/imagenlogoconletras.png" : "/imagenlogosinletras.png";

  const background = {
    position: "fixed",
    inset: 0,
    zIndex: 0,
    backgroundImage: `linear-gradient(rgba(7,7,10,0.86), rgba(7,7,10,0.86)), url("${bgImage}")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center 62%",
    backgroundSize: "cover",
    backgroundColor: "#07070a",
  };

  const backgroundidle = {
    position: "fixed",
    inset: 0,
    zIndex: 0,
    backgroundImage: `url("${bgImage}")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center 35%",
    backgroundSize: "120% auto",
    backgroundColor: "#07070a",
  };

  const root = {
    width: "100vw",
    height: "100vh",
    position: "relative",
    overflow: "hidden",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
  };

  const panel = {
    width: "97vw",
    height: "94vh",
    borderRadius: 28,
    background:
      "radial-gradient(900px 500px at 15% 20%, rgba(255,255,255,0.10), transparent), rgba(255,255,255,0.06)",
    boxShadow: "0 18px 70px rgba(0,0,0,0.55)",
    padding: "1vh 4vw",
    display: "flex",
    flexDirection: "column",
    position: "relative",
    zIndex: 1,
  };

  const topbar = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: "6vh",
  };

  const topbarTitle = {
    fontWeight: 950,
    color: BRAND_RED,
    letterSpacing: "0.08em",
  };

  const priceBig = {
    fontSize: "clamp(8vh, 12vh, 16vh)",
    fontWeight: 1000,
    letterSpacing: "-0.03em",
    lineHeight: 1,
    margin: "2vh 0 1vh",
    color: "#ffffff",
    textShadow: "0 6px 24px rgba(0,0,0,0.45)",
  };

  const labelText = {
    fontSize: "3.4vh",
    color: "rgba(255,255,255,0.75)",
  };

  const barWrap = {
    marginTop: "3vh",
    height: "6vh",
    borderRadius: 999,
    background: "rgba(255,255,255,0.10)",
    overflow: "hidden",
    border: "2px solid rgba(255,255,255,0.10)",
  };

  const footer = {
    marginTop: "auto",
    display: "flex",
    justifyContent: "space-between",
    fontSize: "6vh",
    color: "rgba(255, 255, 255, 1)",
  };

  const audioOverlay = !audioReady ? (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: 16,
        textAlign: "center",
        padding: 20,
      }}
    >
      <div style={{ fontSize: 28, fontWeight: 900 }}>Activar sonido</div>
      <div style={{ fontSize: 18, opacity: 0.85, maxWidth: 520 }}>
        Pulsa una vez para permitir audio en este navegador.
      </div>

      <button
        onClick={unlockAudioNow}
        style={{
          padding: "14px 22px",
          fontSize: 18,
          borderRadius: 12,
          border: "none",
          cursor: "pointer",
          fontWeight: 800,
        }}
      >
        ACTIVAR
      </button>
    </div>
  ) : null;

  // ===== RENDERS =====
  if (mode === "idle") {
    return (
      <div style={root}>
        {audioOverlay}
        <div style={backgroundidle} />
        <div style={{ ...panel, justifyContent: "flex-start", textAlign: "center" }}>
          <div
            style={{
              marginTop: "6vh",
              fontSize: "10vh",
              fontWeight: 950,
              color: BRAND_RED,
              textShadow: "0 4px 20px rgba(0,0,0,0.6)",
            }}
          >
            WhatsApp:&nbsp;<span style={{ color: BRAND_WHITE }}>{BRAND.whatsapp}</span>
          </div>
        </div>
      </div>
    );
  }

  if (mode === "venta") {
    const winners = Array.isArray(ventaState?.winners) ? ventaState.winners : [];
    const soldCount = winners.length;
    const targetQty = Number(ventaState?.qty) || soldCount || 1;
    const isMultiple = targetQty > 1;

    return (
      <div style={root}>
        {audioOverlay}
        <div style={background} />
        <div style={panel}>
          <div style={topbar}>
            <div style={topbarTitle}>VENTA</div>
          </div>

          <div style={{ marginTop: "6vh" }}>
            <div style={labelText}>Precio</div>
            <div style={priceBig}>{ventaPriceText}</div>

            <div style={{ marginTop: "1vh", fontSize: "3vh", opacity: 0.85 }}>
              {isMultiple
                ? <><strong>{soldCount}</strong> de <strong>{targetQty}</strong> unidades vendidas</>
                : <>Escribe <strong>"mio"</strong> para comprar</>}
            </div>

            <div style={{ marginTop: "2vh", fontSize: "3vh", opacity: 0.8 }}>
              Tiempo restante: <strong>{ventaRemaining ?? "—"}s</strong>
            </div>

            <div style={barWrap}>
              <div
                style={{
                  height: "100%",
                  width: `${Math.max(0, Math.min(1, ventaProgress)) * 100}%`,
                  background: `linear-gradient(90deg, ${BRAND_RED}, #ff4b4b)`,
                  transition: "width 100ms linear",
                }}
              />
            </div>

            {/* Avatares de ganadores en tiempo real (venta múltiple) */}
            {isMultiple && winners.length > 0 && (
              <div style={{
                marginTop: "2.5vh",
                display: "flex",
                flexWrap: "wrap",
                gap: "2vh",
                alignItems: "center",
              }}>
                {winners.map((w, idx) => (
                  <div key={w?.userId || idx} style={{
                    animation: "tvBubblePop 0.3s cubic-bezier(.34,1.56,.64,1) both",
                    animationDelay: `${idx * 0.06}s`,
                  }}>
                    {w?.profilePictureUrl
                      ? <img src={w.profilePictureUrl} alt="" style={{ width: "14vh", height: "14vh", borderRadius: "50%", objectFit: "cover", border: "3px solid rgba(255,255,255,0.4)", display: "block" }} />
                      : <div style={{ width: "14vh", height: "14vh", borderRadius: "50%", background: "rgba(255,255,255,0.18)", border: "3px solid rgba(255,255,255,0.2)" }} />
                    }
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={footer}>
            <div>WhatsApp: {BRAND.whatsapp}</div>
            <div style={{ color: BRAND_RED, fontWeight: 3000 }}>{BRAND.companyName}</div>
          </div>
        </div>
      </div>
    );
  }

  if (mode === "ventaSold") {
    const winners = Array.isArray(ventaState?.winners) ? ventaState.winners : [];
    const soldCount = winners.length;
    const targetQty = Number(ventaState?.qty) || soldCount || 1;

    const scale = Math.max(0.6, 1 - (soldCount - 1) * 0.12);
    const TV = 1.25;

    const cardMin = Math.max(30, 46 * scale);
    const winnersGrid = {
      display: "grid",
      gap: `${Math.max(2.2, 3.2 * scale) * TV}vh`,
      marginTop: "4.5vh",
      gridTemplateColumns: `repeat(auto-fit, minmax(${cardMin}vh, 1fr))`,
      alignItems: "stretch",
    };

    const winnerCard = {
      borderRadius: 26,
      background: "rgba(255,255,255,0.08)",
      border: "1px solid rgba(255,255,255,0.16)",
      boxShadow: "0 18px 55px rgba(0,0,0,0.45)",
      padding: `${Math.max(2.2, 3.2 * scale) * TV}vh`,
      display: "flex",
      gap: `${Math.max(2.0, 2.8 * scale) * TV}vh`,
      alignItems: "center",
      minHeight: `${Math.max(20, 28 * scale) * TV}vh`,
      width: "100%",
    };

    const winnerAvatar = {
      width: `${Math.max(10, 16 * scale) * TV}vh`,
      height: `${Math.max(10, 16 * scale) * TV}vh`,
      borderRadius: "50%",
      objectFit: "cover",
      border: "3px solid rgba(255,255,255,0.24)",
      flex: "0 0 auto",
    };

    const winnerName = {
      fontSize: `${Math.max(3.8, 7.2 * scale) * TV}vh`,
      fontWeight: 1000,
      lineHeight: 1.5,
      whiteSpace: "normal",
      overflow: "hidden",
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      wordBreak: "break-word",
    };

    return (
      <div style={root}>
        {audioOverlay}
        <div style={background} />
        <div style={panel}>
          <div style={topbar}>
            <div style={topbarTitle}>VENTA</div>
          </div>

          <div style={{ marginTop: "6vh" }}>
            <div style={{ fontSize: "3.4vh", opacity: 0.85 }}>Vendido por</div>
            <div style={priceBig}>{ventaPriceText}</div>

            <div style={{ marginTop: "1vh", fontSize: "3vh", opacity: 0.8 }}>
              Ganadores: <strong>{soldCount}/{targetQty}</strong>
            </div>

            <div style={winnersGrid}>
              {winners.map((w, idx) => {
                const name = w?.nickname || w?.uniqueId || "—";
                const photo = w?.profilePictureUrl || null;

                return (
                  <div key={`${w?.userId || "u"}-${idx}`} style={winnerCard}>
                    {photo ? (
                      <img src={photo} alt="perfil" style={winnerAvatar} />
                    ) : (
                      <div style={{ ...winnerAvatar, background: "rgba(255,255,255,0.10)" }} />
                    )}

                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: `${Math.max(2.0, 2.8 * scale) * TV}vh`, opacity: 0.75 }}>
                        #{idx + 1}
                      </div>
                      <div style={winnerName}>{name}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop: "2.5vh", fontSize: "2.8vh", opacity: 0.7 }}>
              Volviendo a inicio en 10s…
            </div>
          </div>

          <div style={footer}>
            <div>WhatsApp: {BRAND.whatsapp}</div>
            <div style={{ color: BRAND_RED, fontWeight: 3000 }}>{BRAND.companyName}</div>
          </div>
        </div>
      </div>
    );
  }

  if (mode === "ventaTimeout") {
    return (
      <div style={root}>
        {audioOverlay}
        <div style={background} />
        <div style={{ ...panel, justifyContent: "center" }}>
          <div style={topbar}>
            <div style={topbarTitle}>VENTA</div>
          </div>

          <div style={{ marginTop: "6vh" }}>
            <div style={{ fontSize: "5vh", fontWeight: 950 }}>Tiempo agotado</div>
            <div style={{ marginTop: "2vh", fontSize: "3vh", opacity: 0.8 }}>No hubo comprador.</div>
            <div style={{ marginTop: "3vh", fontSize: "2.8vh", opacity: 0.7 }}>
              Volviendo a inicio en 10s…
            </div>
          </div>

          <div style={footer}>
            <div>WhatsApp: {BRAND.whatsapp}</div>
            <div style={{ color: BRAND_RED, fontWeight: 3000 }}>{BRAND.companyName}</div>
          </div>
        </div>
      </div>
    );
  }

  if (mode === "subastaTimeout") {
    return (
      <div style={root}>
        {audioOverlay}
        <div style={background} />
        <div style={{ ...panel, justifyContent: "center" }}>
          <div style={topbar}>
            <div style={topbarTitle}>SUBASTA</div>
          </div>

          <div style={{ marginTop: "6vh" }}>
            <div style={{ fontSize: "5vh", fontWeight: 950 }}>Tiempo agotado</div>
            <div style={{ marginTop: "2vh", fontSize: "3vh", opacity: 0.8 }}>
              Subasta no adjudicada por timeout.
            </div>
            <div style={{ marginTop: "3vh", fontSize: "2.8vh", opacity: 0.7 }}>
              Volviendo a inicio en 10s…
            </div>
          </div>

          <div style={footer}>
            <div>WhatsApp: {BRAND.whatsapp}</div>
            <div style={{ color: BRAND_RED, fontWeight: 3000 }}>{BRAND.companyName}</div>
          </div>
        </div>
      </div>
    );
  }

  if (mode === "subastaEnded") {
    const lb = Array.isArray(subastaState?.leaderboard) ? subastaState.leaderboard : [];
    // Usar el winner del evento como fallback si el estado aún no se ha actualizado
    const winner = lb[0] || subastaEndedWinnerRef.current || null;
    const next5 = lb.slice(1, 6);

    const winnerName = winner?.nickname || winner?.uniqueId || "—";
    const winnerAmount = winner?.amount != null ? `${winner.amount}€` : "—";
    const winnerPhoto = winner?.profilePictureUrl || null;

    return (
      <div style={root}>
        {audioOverlay}
        <div style={background} />
        <div style={panel}>
          <div style={topbar}>
            <div style={topbarTitle}>SUBASTA</div>
          </div>

          <div style={{ marginTop: "4vh" }}>
            <div style={{ fontSize: "4.2vh", opacity: 0.85 }}>GANADOR</div>

            <div style={{ marginTop: "1.2vh", fontSize: "3.6vh", opacity: 0.8 }}>
              Puja mínima: <strong>{subastaMinBidText}</strong>
            </div>

            <div style={{ marginTop: "2.2vh", display: "flex", gap: "4vh", alignItems: "center" }}>
              {winnerPhoto ? (
                <img
                  src={winnerPhoto}
                  alt="perfil"
                  style={{
                    width: "22vh",
                    height: "22vh",
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "3px solid rgba(255,255,255,0.25)",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "22vh",
                    height: "22vh",
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.10)",
                    border: "3px solid rgba(255,255,255,0.15)",
                  }}
                />
              )}

              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "14vh", opacity: 0.95, marginTop: "1vh", fontWeight: 950 }}>
                  {winnerName}
                </div>
                <div style={{ fontSize: "10vh", fontWeight: 1000, lineHeight: 1 }}>{winnerAmount}</div>

                <div style={{ marginTop: "1.8vh", fontSize: "3.6vh", opacity: 0.7 }}>
                  Volviendo a inicio en 10s…
                </div>
              </div>
            </div>

            <div style={{ marginTop: "4vh" }}>
              <div style={{ fontSize: "3.8vh", fontWeight: 900, opacity: 0.9 }}>Siguientes 5 pujantes</div>

              {next5.map((b, idx) => {
                const name = b?.nickname || b?.uniqueId || "—";
                const amt = b?.amount != null ? `${b.amount}€` : "—";
                const photo = b?.profilePictureUrl || null;

                return (
                  <div
                    key={`${b?.userId || "u"}-${idx}-${b?.amount || 0}`}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "1.8vh 2.2vh",
                      borderRadius: 18,
                      background: "rgba(255,255,255,0.06)",
                      border: "1px solid rgba(255,255,255,0.10)",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "1.6vh", minWidth: 0 }}>
                      <div style={{ fontSize: "3.2vh", opacity: 0.85, width: "5vh", textAlign: "right" }}>
                        {idx + 2}.
                      </div>

                      {photo ? (
                        <img
                          src={photo}
                          alt="perfil"
                          style={{
                            width: "6.5vh",
                            height: "6.5vh",
                            borderRadius: "50%",
                            objectFit: "cover",
                            border: "1px solid rgba(255,255,255,0.18)",
                            flex: "0 0 auto",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: "6.5vh",
                            height: "6.5vh",
                            borderRadius: "50%",
                            background: "rgba(255,255,255,0.10)",
                            border: "1px solid rgba(255,255,255,0.12)",
                            flex: "0 0 auto",
                          }}
                        />
                      )}

                      <div
                        style={{
                          fontSize: "3.4vh",
                          opacity: 0.95,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {name}
                      </div>
                    </div>

                    <div style={{ fontSize: "3.8vh", fontWeight: 1000, marginLeft: "2vh", flex: "0 0 auto" }}>
                      {amt}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={footer}>
            <div>WhatsApp: {BRAND.whatsapp}</div>
            <div style={{ color: BRAND_RED, fontWeight: 3000 }}>{BRAND.companyName}</div>
          </div>
        </div>
      </div>
    );
  }

  // Default: SUBASTA en curso
  const top = subastaState?.top;
  const topName = top?.nickname || top?.uniqueId || "—";
  const topAmount = top?.amount != null ? `${top.amount}€` : "—";
  const topPhoto = top?.profilePictureUrl || null;

  return (
    <div style={root}>
      {audioOverlay}
      <div style={background} />
      <div style={panel}>
        <div style={topbar}>
          <div style={topbarTitle}>SUBASTA</div>
        </div>

        <div style={{ marginTop: "7vh" }}>
          <div style={{ fontSize: "4.0vh", opacity: 0.85 }}>Mejor puja</div>

          <div style={{ marginTop: "1.2vh", fontSize: "3.6vh", opacity: 0.85 }}>
            Puja mínima: <strong>{subastaMinBidText}</strong>
          </div>

          <div style={{ marginTop: "2.5vh", display: "flex", gap: "4vh", alignItems: "center" }}>
            {topPhoto ? (
              <img
                src={topPhoto}
                alt="perfil"
                style={{
                  width: "18vh",
                  height: "18vh",
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "3px solid rgba(255,255,255,0.20)",
                  flex: "0 0 auto",
                }}
              />
            ) : (
              <div
                style={{
                  width: "18vh",
                  height: "18vh",
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.10)",
                  border: "3px solid rgba(255,255,255,0.12)",
                  flex: "0 0 auto",
                }}
              />
            )}

            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: "12vh", fontWeight: 1000, lineHeight: 1 }}>{topAmount}</div>
              <div
                style={{
                  fontSize: "7vh",
                  opacity: 0.95,
                  marginTop: "1.5vh",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontWeight: 950,
                }}
              >
                {topName}
              </div>
            </div>
          </div>

          <div style={{ marginTop: "4vh", fontSize: "3.6vh", opacity: 0.8 }}>
            Tiempo restante: <strong>{subastaRemaining ?? "—"}s</strong>
          </div>

          <div style={barWrap}>
            <div
              style={{
                height: "100%",
                width: `${Math.max(0, Math.min(1, subastaProgress)) * 100}%`,
                background: `linear-gradient(90deg, ${BRAND_RED}, #ff4b4b)`,
                transition: "width 100ms linear",
              }}
            />
          </div>
        </div>

        <div style={footer}>
          <div>WhatsApp: {BRAND.whatsapp}</div>
          <div style={{ color: BRAND_RED, fontWeight: 3000 }}>{BRAND.companyName}</div>
        </div>
      </div>
    </div>
  );
}
